import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import {
  INITIAL_LIMIT,
  INITIAL_PAGE,
} from '@atom/components/common/requests/assetRequestConstants';
import {
  cleanFilters,
  getCustomValues,
  getTypeChangeFilters,
} from '@atom/components/common/requests/customTenantUtilities';
import RequestsTable from '@atom/components/common/requests/requestsTable/RequestsTable';
import ViewRequestModal from '@atom/components/common/requests/viewRequest/ViewRequestModal';
import { GET_ASSET_REQUESTS } from '@atom/graph/assetRequest';
import { GET_SCHEMA } from '@atom/graph/schema';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Progress, Snackbar } from '@atom/mui';
import {
  AssetRequestsFilters,
  AssetRequestType,
} from '@atom/types/assetRequest';

import InventoryDetailContext from '../InventoryDetailContext';

import RequestsFilters from './requestsFilters/RequestsFilters';
import RequestsContext from './RequestsContext';
import RequestsHeader from './RequestsHeader';

import classes from './requestsTab.module.css';

type AppliedFilters = {
  assetId: string;
  filters: AssetRequestsFilters;
};

const RequestsTab = () => {
  const { asset, refetch } = useContext(InventoryDetailContext);

  const [{ activeRequestId }, { updateSearchParams }] = useNavigation({
    activeRequestId: SearchParamType.STRING,
  });
  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [limit, setLimit] = useState<number>(INITIAL_LIMIT);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>();
  const [filtersCart, setFiltersCart] = useState<AssetRequestsFilters>({});

  const {
    data: schemaData,
    loading: loadingSchema,
    error: schemaError,
  } = useQuery(GET_SCHEMA, {
    variables: { id: asset.schemaId },
    fetchPolicy: 'network-only',
    skip: !asset.schemaId,
  });

  useEffect(() => {
    if (schemaError) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    }
  }, [schemaError]);

  const loadedSchema = schemaData?.schema;
  const schema = R.propEq(asset.schemaId, 'id', loadedSchema)
    ? loadedSchema
    : undefined;

  const { initialFilters, initialSort } = getCustomValues(schema, asset);

  const [sortBy, setSortBy] = useState<string>(initialSort.sortBy);
  const [isAscending, setIsAscending] = useState<boolean>(
    initialSort.isAscending,
  );

  const applied = R.propEq(asset.id, 'assetId', appliedFilters)
    ? appliedFilters
    : undefined;
  const filters = applied?.filters ?? initialFilters;

  useEffect(() => {
    setFiltersCart(initialFilters);
  }, [asset.id, schema?.id]);

  const {
    data,
    loading: assetRequestsLoading,
    refetch: refetchAssetRequests,
  } = useQuery(GET_ASSET_REQUESTS, {
    variables: {
      input: {
        page,
        limit,
        sortBy,
        isAscending,
        ...filters,
      },
    },
    fetchPolicy: 'network-only',
    skip: !schema,
  });

  const setActiveRequestId = (id: string) => {
    updateSearchParams({ activeRequestId: id });
  };

  const resetInitialValues = () => {
    setPage(INITIAL_PAGE);
    setLimit(INITIAL_LIMIT);
    setSortBy(initialSort.sortBy);
    setIsAscending(initialSort.isAscending);
  };

  const resetFilters = () => {
    resetInitialValues();
    setAppliedFilters(undefined);
    setFiltersCart(initialFilters);
  };

  // When type is changed, filters should be automatically applied.
  const changeType = (type: AssetRequestType) => {
    const updatedFilter = getTypeChangeFilters(type, asset);

    // Handles refetching if a new request is created without changing types.
    if (type === filters?.type) {
      refetchAssetRequests();
    }

    setFiltersCart(updatedFilter);
    setAppliedFilters({
      assetId: asset.id,
      filters: cleanFilters(updatedFilter),
    });
  };

  const applyFilters = () => {
    resetInitialValues();
    setAppliedFilters({
      assetId: asset.id,
      filters: cleanFilters(filtersCart),
    });
  };

  const totalCount = R.pathOr(0, ['assetRequests', 'totalCount'], data);
  const requests = R.pathOr([], ['assetRequests', 'assetRequests'], data);
  const loading = loadingSchema || assetRequestsLoading;

  return (
    <RequestsContext.Provider
      value={{
        asset,
        schema,
        loadingSchema,
        requests,
        totalCount,
        loading,
        filters,
        filtersCart,
        setFiltersCart,
        page,
        setPage,
        limit,
        setLimit,
        sortBy,
        setSortBy,
        isAscending,
        setIsAscending,
        changeType,
        setActiveRequestId,
      }}
    >
      <>
        <div className={classes.tabContent}>
          <div className={classes.leftBodyPaneFixed}>
            <RequestsFilters
              applyFilters={applyFilters}
              resetFilters={resetFilters}
            />
          </div>
          <div className={classes.rightBodyPaneFill}>
            <RequestsHeader />
            {loading ? (
              <div className={classes.progressContainer}>
                <Progress />
              </div>
            ) : (
              <RequestsTable
                requests={requests}
                totalCount={totalCount}
                filters={filters}
                page={page}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                sortBy={sortBy}
                setSortBy={setSortBy}
                isAscending={isAscending}
                setIsAscending={setIsAscending}
                setActiveRequestId={setActiveRequestId}
                offsetTop="325px"
                assetId={asset?.id}
              />
            )}
          </div>
        </div>
        <ViewRequestModal
          activeRequestId={activeRequestId}
          setActiveRequestId={setActiveRequestId}
          changeType={changeType}
          onAssetRefresh={() => refetch().catch(() => undefined)}
        />
      </>
    </RequestsContext.Provider>
  );
};

export default RequestsTab;
